import React, { ChangeEvent, FC, FormEvent, useEffect, useState } from "react";
import Breadcrumb, { Crumb } from "../../../../components/common/Breadcrumbs";
import { useNavigate, useParams } from "react-router-dom";
import { useGetEnumsQuery } from "../../../../app/globalApi";
import { getEnumDisplayNameByConstant } from "../../../../helpers/utils/EnumUtils";
import { handleRTKQuery } from "../../../../helpers/utils/RtkQueryUtils";
import ModalButton from "../../../../components/common/form/ModalButton";
import IconButton from "../../../../components/common/form/IconButton";
import { FaMagnifyingGlass, FaTrash } from "react-icons/fa6";
import ConfirmationModal from "../../../../components/layout/modal/ConfirmationModal";
import AuditableInfoButton from "../../../../components/common/form/AuditableInfoButton";
import Form from "../../../../components/common/form/Form";
import InputField from "../../../../components/common/form/InputField";
import TextAreaField from "../../../../components/common/form/TextAreaField";
import SelectField from "../../../../components/common/form/SelectField";
import { createOptionListForSelectTag } from "../../../../helpers/utils/Utils";
import ButtonTray from "../../../../components/common/form/ButtonTray";
import { hasPermission } from "../../../../helpers/utils/AccessControlUtils";
import { Routes as AppRoutes } from "../../../../routes";
import { FaLink, FaSearch, FaTimes } from "react-icons/fa";
import CommonSelectorModal from "../../../../components/common/form/CommonSelectorModal";
import { DataTableColumn } from "../../../../components/common/datatable/DataTableTypes";
import { useSearchCollectorQuery } from "../../../data/collector/collector";
import {
    useCreateAccountTransMutation,
    useDeleteAccountTransByIdMutation,
    useGetAccountTransByIdQuery,
    useUpdateAccountTransMutation
} from "../accountTransApi";
import { skipToken } from "@reduxjs/toolkit/query";

interface Props {
    crumbs: Crumb[];
    title: string;
    btnText: string;
    isReadOnly?: boolean;
}

const AccounTransForm: FC<Props> = ({ crumbs, title, btnText, isReadOnly }) => {

    const navigate = useNavigate();
    let { accountId } = useParams();

    const accountTransApi = useGetAccountTransByIdQuery(accountId ?? skipToken);
    const [accountTransData, setAccountTransData] = useState<any>({});
    const [updateaccountTransApi, updateaccountTransApiResponse] = useUpdateAccountTransMutation()
    const [createaccountTransApi, createaccountTransApiResponse] = useCreateAccountTransMutation()
    const { data: enums } = useGetEnumsQuery("");
    const [deleteAccountTrans, { isLoading }] = useDeleteAccountTransByIdMutation();
    const iconButtonStyle = "fa cursor-pointer text-highlight-0 absolute left-auto right-5 top-6";
    const [showModal, setShowModal] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState<any>({});
    const [modalName, setModalName] = useState<any>("");
    const [sourceCollectorId, setSourceCollectorId] = useState("");
    const [destinationCollectorId, setDestinationCollectorId] = useState("");
    const [selectedRow, setSelectedRow] = useState<any>({});
    const [showSourceCollectorModal, setShowSourceCollectorModal] = useState<boolean>(false);

    useEffect(() => {
        const obj = accountTransApi?.data;
        if (obj && enums) {
            // Set collector IDs separately
            setSourceCollectorId(obj?.sourceCollector?.id || '');
            setDestinationCollectorId(obj?.destinationCollector?.id || '');

            // Set account transaction data
            setAccountTransData({
                ...obj,
                sourceTarget: obj.sourceTarget ? {
                    value: obj.sourceTarget,
                    label: getEnumDisplayNameByConstant(enums["AccountTranslationTarget"], obj.sourceTarget),
                } : null,
                destinationTarget: obj.destinationTarget ? {
                    value: obj.destinationTarget,
                    label: getEnumDisplayNameByConstant(enums["AccountTranslationTarget"], obj.destinationTarget),
                } : null,
                accountNumberType: obj.accountNumberType ? {
                    value: obj.accountNumberType,
                    label: getEnumDisplayNameByConstant(enums["AccountNumberType"], obj.accountNumberType),
                } : null,
                destinationCollector: obj?.destinationCollector?.name || '',
                sourceCollector: obj?.sourceCollector?.name || '',
                translationCommand: obj.translationCommand || '',
            });
        }
    }, [accountTransApi?.data, enums]);

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        let obj = {
            translationCommand: accountTransData.translationCommand,
            sourceTarget: accountTransData.sourceTarget?.value,
            destinationTarget: accountTransData.destinationTarget?.value,
            accountNumberType: accountTransData.accountNumberType?.value,
            sourceCollectorId: sourceCollectorId,
            destinationCollectorId: destinationCollectorId,
        };
        const apiCall = async () => {
            if (btnText !== "Update") {
                return await createaccountTransApi(
                    obj,
                ).unwrap();

            } else {
                return await updateaccountTransApi({
                    id: accountId,
                    requestBody: obj,
                }).unwrap();
            }
        };

        await handleRTKQuery(apiCall, (response: any) => {
            if (response?.success || response?.data?.success) {
                setAccountTransData({});
                navigate(AppRoutes.AccountTranslations.path);
            }
        });
    };


    const handleOnChange = (e: ChangeEvent<HTMLInputElement> | {
        name: string;
        value: any
    } | ChangeEvent<HTMLTextAreaElement>) => {
        const { name, value } = "target" in e ? e.target : e;
        setAccountTransData({
            ...accountTransData,
            [name]: value,
        })
    }

    const handleDelete = async () => {
        if (!accountId) return;

        try {
            await deleteAccountTrans(accountId).unwrap();
            navigate(AppRoutes.AccountTranslations.path); // Only runs if delete succeeds
        } catch (error) {
            console.error("Delete failed:", error);
            // Optionally show a toast or error message here
        }
    };

    const handleClearData = (name: any) => {
        setAccountTransData((prevData: any) => ({
            ...prevData,
            [name]: '',
        }));
        if (name === "sourceCollector") {
            setSourceCollectorId("");
        } else if (name === "destinationCollector") {
            setDestinationCollectorId("");
        }
    };

    const handleOnClick = (e: any, key: string) => {
        e.preventDefault();

        const updatedData: any = {
            ...accountTransData,
            [key]: selectedRow.name
        };
        if (key === "sourceCollector") {
            setSourceCollectorId(selectedRow.id);
        } else if (key === "destinationCollector") {
            setDestinationCollectorId(selectedRow.id);
        }
        setAccountTransData(updatedData);
        setShowModal(false);
    };

    const columns: DataTableColumn[] = [
        {
            label: "Collecto Name",
            accessor: "name",
            filterOptions: [{
                filterModalOption: {
                    filterModalPlaceHolder: "Enter Name...",
                    filterModalKey: "name",
                    filterModalIcon: <FaMagnifyingGlass size={14} className={''} />
                },
                overrideFilterLabel: " ",
                overrideFilterAccessor: "name"
            }],

        },
        {
            label: "State",
            accessor: "state.stateAbbreviation",
        },
        {
            label: "County",
            accessor: "countyName",
        }
    ]

    return (
        <>
            <div className="px-4 py-2">

                <div className={"justify-between sm:block md:flex lg:flex"}>
                    <Breadcrumb crumbs={crumbs} />
                    <div className={"justify-between sm:block md:flex lg:flex"}>
                        {accountId && hasPermission(["ACCTRA-D"]) && (
                            <>
                                <ModalButton
                                    triggerButton={
                                        <IconButton
                                            icon={<FaTrash />}
                                            title={"Delete Auto Hoa"}
                                            hasBackground={true}
                                            className="mr-2"
                                        />
                                    }
                                    modal={
                                        <ConfirmationModal
                                            show={true}
                                            setShow={() => {
                                            }}
                                            title={"Delete Account Translation"}
                                            bodyMessage={"Are you sure you want to delete this Account Translation?"}
                                            onSubmit={handleDelete}
                                            isLoading={isLoading}
                                        />
                                    }
                                />
                                {
                                    hasPermission(["AUDIT-V"]) &&
                                    <AuditableInfoButton obj={accountTransApi?.data} />}
                            </>
                        )}
                    </div>
                </div>
                <div className="flex justify-center mt-3">
                    <div
                        className="bg-text-0 lg:w-4/6 sm:w-full p-5  rounded-md shadow-[0_0_10px_-2px_rgba(0,0,0,0.2)]">
                        <h3 className="text-xl font-semibold text-surface-0 pb-3 border-b border-surface-5">{title}</h3>
                        <Form onSubmit={handleSubmit} fields={<>
                            <div className="grid grid-cols-12 text-sm">
                                <InputField
                                    name="sourceCollector"
                                    label="Source Collector"
                                    onChange={handleOnChange}
                                    value={accountTransData?.sourceCollector || ""}
                                    type="text"
                                    placeholder="Enter Source Collector..."
                                    required={true}
                                    disabled={isReadOnly}
                                    icon={<IconButton
                                        icon={<FaLink onClick={() => {
                                            if (sourceCollectorId) {
                                                window.open(`/collectors/${sourceCollectorId}`, '_blank');
                                            } else {
                                                window.open(`/collectors`, '_blank');
                                            }
                                        }} />
                                        }
                                        className={`text-sm  ${accountTransData?.sourceCollector ? 'text-highlight-4' : 'text-surface-4 cursor-default'
                                            }`}
                                        title={"Go to Collector"}
                                    />}
                                    className="cursor-pointer"
                                    onClick={() => {
                                        setShowModal(true);
                                        setModalName("sourceCollector");
                                    }}

                                    inputIcon={
                                        accountTransData?.sourceCollector ? (
                                            <FaTimes
                                                className={iconButtonStyle}
                                                title="Clear Data"
                                                onClick={() => {
                                                    handleClearData("sourceCollector");
                                                }}
                                            />
                                        ) : (
                                            <FaSearch
                                                className={iconButtonStyle}
                                                title="Search Collector"
                                                onClick={() => {
                                                    if (!isReadOnly) {
                                                        setShowModal(true);
                                                        setModalName("sourceCollector");
                                                    }

                                                }}
                                            />

                                        )
                                    }


                                />

                                <InputField
                                    name="destinationCollector"
                                    label="Desitnation Collector"
                                    onChange={handleOnChange}
                                    value={accountTransData?.destinationCollector || ""}
                                    type="text"
                                    disabled={isReadOnly}
                                    placeholder="Enter Destination Collector..."
                                    required={true}
                                    icon={<IconButton
                                        icon={<FaLink onClick={() => {
                                            if (destinationCollectorId) {
                                                window.open(`/collectors/${destinationCollectorId}`, '_blank');
                                            } else {
                                                window.open(`/collectors`, '_blank');
                                            }
                                        }} />
                                        }
                                        className={`text-sm ${accountTransData?.destinationCollector ? 'text-highlight-4' : 'text-surface-4 cursor-default'
                                            }`}
                                        title={"Go to Collector"}
                                    />}
                                    className="cursor-pointer"
                                    onClick={() => {
                                        setShowModal(true);
                                        setModalName("destinationCollector");
                                    }}
                                    inputIcon={
                                        accountTransData?.destinationCollector ? (
                                            <FaTimes
                                                className={iconButtonStyle}
                                                title="Clear Data"
                                                onClick={() => {
                                                    handleClearData("destinationCollector");
                                                }}
                                            />
                                        ) : (
                                            <FaSearch
                                                className={iconButtonStyle}
                                                title="Search Collector"
                                                onClick={() => {
                                                    if (!isReadOnly) {
                                                        setShowModal(true);
                                                        setModalName("destinationCollector");
                                                    }

                                                }}
                                            />
                                        )
                                    }


                                />
                                <SelectField
                                    placeholder='Select Source Target...'
                                    value={accountTransData?.sourceTarget || null}
                                    options={createOptionListForSelectTag(enums?.["AccountTranslationTarget"], "value", "constant")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({ name: "sourceTarget", value: selectedOption })
                                    }}
                                    required={true}
                                    label={"Source Target"}
                                    isDisabled={isReadOnly}
                                />
                                <SelectField
                                    placeholder='Select Destination Target...'
                                    value={accountTransData?.destinationTarget || null}
                                    options={createOptionListForSelectTag(enums?.["AccountTranslationTarget"], "value", "constant")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({ name: "destinationTarget", value: selectedOption })
                                    }}
                                    required={true}
                                    label={"Destination Target"}
                                    isDisabled={isReadOnly}
                                />

                                <SelectField
                                    placeholder='Select Account Number Type...'
                                    value={accountTransData?.accountNumberType || null}
                                    options={createOptionListForSelectTag(enums?.["AccountNumberType"], "value", "constant")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({ name: "accountNumberType", value: selectedOption })
                                    }}
                                    isDisabled={isReadOnly}
                                    label={"Account Number Type"}
                                />

                                <TextAreaField
                                    label={"Translation Command"}
                                    placeholder={"Enter Translation Command...."}
                                    onChange={handleOnChange}
                                    name={"translationCommand"}
                                    value={accountTransData?.translationCommand || ""}
                                    disabled={isReadOnly}
                                />


                            </div>
                            {!isReadOnly &&



                                <div className="col-span-12 md:grid md:grid-cols-12 gap-3">
                                    <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                                    </div>
                                    <div className="relative col-span-9">
                                        <div className={"mt-4 flex"}>
                                            <ButtonTray
                                                buttons={[
                                                    {
                                                        buttonProps: {
                                                            btnText: "Cancel",
                                                            onClick: () => navigate(AppRoutes.AccountTranslations.path),
                                                            type: "close",
                                                        },
                                                        buttonType: "button"
                                                    },
                                                    {
                                                        buttonProps: {
                                                            btnText: "Submit",
                                                            type: "submit",
                                                            isLoading: (createaccountTransApiResponse.isLoading || updateaccountTransApiResponse.isLoading),
                                                            isVisible: hasPermission(["CANNOTE-U"])
                                                        },
                                                        buttonType: "button",

                                                    }
                                                ]}
                                                align="center"
                                                gap={2}
                                            />
                                        </div>
                                    </div>
                                </div>
                            }
                        </>
                        } />
                    </div>
                </div>
                {showModal && (
                    <CommonSelectorModal
                        title="Collector Selector"
                        columns={columns}
                        data={useSearchCollectorQuery}
                        show={true}
                        setId={(selectedRows: any) => {
                            setSelectedRowData(selectedRows);
                        }}
                        onSubmit={() => handleOnClick(selectedRowData, modalName)}
                        setShow={() => {
                        }}
                    />
                )}
            </div>
            {showModal &&
                <CommonSelectorModal
                    title="Collector Selector"
                    columns={columns}
                    data={useSearchCollectorQuery}
                    show={showModal}
                    onSubmit={(e: any) => handleOnClick(e, modalName)}
                    setShow={setShowModal}
                    setSelectedRow={setSelectedRow}
                />
            }
        </>
    )
}

export default AccounTransForm;